import { useEffect, useMemo, useRef, useState } from "react";

import Button from "../../../../shared/components/elements/Button";
import { useContentCompletion } from "../../hooks/useContentCompletion";
import { useProjectProgress } from "../../hooks/useProjectProgress";
import { useProjectRunner } from "../../hooks/useProjectRunner";
import { PROJECT_MESSAGES } from "../../messages";
import type { ProjectContent, StepId } from "../../projectTypes";
import { getSlotLabel, getSlotLanguage, getSlots } from "../../../../shared/utils/workspace";
import type { PracticeRequest, WorkspaceType } from "../../projectTypes";
import { flattenSteps } from "../../utils/project";
import type { WorkspaceSlot } from "./ProjectWorkspace";
import CompletionFlow from "../lesson/CompletionFlow";
import HeaderLesson from "../lesson/HeaderLesson";
import ProjectInstructions from "./ProjectInstructions";
import ProjectTimeline from "./ProjectTimeline";
import ProjectWorkspace from "./ProjectWorkspace";

interface ContentProjectProps {
    content?: ProjectContent | null;
    userId?: number | string;
    courseId: string;
    contentId: string;
    lessonAttemptId?: number | string | null; // Null on replays, where it is never used
    alreadyCompleted?: boolean;
    onExit: () => void;
}

// Render with key={content id}: the saved progress is read once when this mounts
const ContentProject = ({
                            content,
                            userId,
                            courseId,
                            contentId,
                            lessonAttemptId,
                            alreadyCompleted = false,
                            onExit
                        }: ContentProjectProps) => {
    const steps = useMemo(() => flattenSteps(content), [content]);

    const workspace = content?.workspace ?? {};
    const type: WorkspaceType = workspace.type ?? "program";

    // The editors of the project, from its template: one for programs and SQL, 1-3 for web
    const slots = useMemo<Omit<WorkspaceSlot, "starterCode">[]>(
        () =>
            getSlots(workspace).map((slot) => ({
                slot,
                label: getSlotLabel(slot) ?? PROJECT_MESSAGES.tabCode,
                language: getSlotLanguage(workspace, slot)
            })),
        [workspace]
    );
    const slotNames = useMemo(() => slots.map((item) => item.slot), [slots]);


    const progress = useProjectProgress(`codepro:project:${userId}:${contentId}`, steps, slotNames);
    const { currentIndex, setCurrentIndex, completedIds, markStepDone, files, setFile, applyStarter, stdin, setStdin } =
        progress;

    const { run, running, output } = useProjectRunner();
    const completion = useContentCompletion({
        userId,
        courseId,
        contentId,
        attemptId: lessonAttemptId,
        alreadyCompleted
    });



    const [failedStepId, setFailedStepId] = useState<StepId | null>(null);
    const [resultTabRequest, setResultTabRequest] = useState(0);
    const [finished, setFinished] = useState(false);

    const currentStep = steps[currentIndex];
    const editorSlots: WorkspaceSlot[] = slots.map((item) => ({
        ...item,
        starterCode:
            currentStep?.editors?.find((editor) => editor.slot === item.slot)
                ?.starterCode ?? ""
    }));

    const language = (content?.workspace?.language ?? "python").toLowerCase();
    const allDone = steps.length > 0 && steps.every((step) => completedIds.has(step.id));

    // A different step starts with its own starter code (the first render keeps the restored code)
    const lastStepId = useRef(currentStep?.id);

    useEffect(() => {
        if (lastStepId.current === currentStep?.id) return;
        lastStepId.current = currentStep?.id;

        applyStarter(currentStep);
        setFailedStepId(null);
    }, [currentStep, applyStarter]);

    // Each kind of project sends what its server route expects
    const buildRequest = (stepId: StepId): PracticeRequest => {
        const base = { content_id: contentId, stepId };

        if (type === "web") {
            return { ...base, kind: "web", editors: { html: files.html ?? "", css: files.css ?? "", js: files.js ?? "" } };
        }
        if (type === "sql") return { ...base, kind: "sql", code: files.sql ?? "" };

        return {
            ...base,
            kind: "program",
            code: files.main ?? "",
            stdin: stdin.replace(/\r\n/g, "\n"),
            checkpoints: progress.checkpoints
        };
    };

    const handleRun = async () => {
        if (!currentStep || running) return;

        // Remember which step was run: the learner may open another one while waiting
        const runStepId = currentStep.id;

        setResultTabRequest((count) => count + 1);
        setFailedStepId(null);

        const outcome = await run(buildRequest(runStepId));
        if (!outcome) return;

        // ...the rest of handleRun is unchanged
    };

    // Save the result first, then open the finish screens
    const handleFinish = async () => {
        if (await completion.complete()) setFinished(true);
    };

    // All hooks are above: this early return is safe
    if (finished) {
        return <CompletionFlow result={completion.result} showRewards={!alreadyCompleted} onExit={onExit} />;
    }

    const failedStep = steps.find((step) => step.id === failedStepId);

    return (
        <div className="min-h-screen flex flex-col">
            <HeaderLesson />

            <main className="flex-1 w-full">
                <div className="w-full h-full px-4 py-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Instructions and timeline */}
                            <div className="min-w-0">
                                <div className="rounded-2xl border h-full block border-gray-200 bg-white p-6">
                                    {allDone && (
                                        <div className="mb-6 rounded-xl bg-green-50 border border-green-200 p-4 flex flex-wrap items-center justify-between gap-4">
                                            <span className="font-semibold text-green-800">
                                                {PROJECT_MESSAGES.allStepsDone}
                                            </span>

                                            <Button onClick={handleFinish} loading={completion.completing}>
                                                <span>{PROJECT_MESSAGES.finishProject}</span>
                                            </Button>
                                        </div>
                                    )}

                                    {completion.error && (
                                        <p role="alert" className="mb-4 text-sm text-red-600">
                                            {completion.error}
                                        </p>
                                    )}

                                    {currentStep ? (
                                        <ProjectInstructions
                                            key={currentStep.id}
                                            step={currentStep}
                                            isCompleted={completedIds.has(currentStep.id)}
                                            failedStep={failedStep}
                                            hasNext={currentIndex < steps.length - 1}
                                            onComplete={() => markStepDone(currentStep.id)}
                                            onNext={() => setCurrentIndex(currentIndex + 1)}
                                            onHintsOpened={completion.recordHint}
                                        />
                                    ) : (
                                        <p className="text-gray-400 text-sm">{PROJECT_MESSAGES.noSteps}</p>
                                    )}

                                    <ProjectTimeline
                                        parts={content?.parts ?? []}
                                        steps={steps}
                                        currentIndex={currentIndex}
                                        completedIds={completedIds}
                                        onSelect={setCurrentIndex}
                                    />
                                </div>
                            </div>

                            {/* Editor */}
                            <ProjectWorkspace
                                type={type}
                                slots={editorSlots}
                                files={files}
                                onFileChange={setFile}
                                stdin={stdin}
                                onStdinChange={setStdin}
                                output={output}
                                running={running}
                                onRun={handleRun}
                                resultTabRequest={resultTabRequest}
                            />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ContentProject;